<template>
    <div class="columns is-multiline">
        <div class="column is-6">
            <div :class="getFormGroupClass('fullName')">
                <label class="label">{{ translate("page.admin_edit_event.full_name") }}</label>
                <input id="eventFullName" class="input" v-model="fullName" maxlength="65" />
                <span class="help">{{ getValidationMessage("fullName") }}</span>
            </div>
        </div>
        <div class="column is-6">
            <div class="field">
                <label class="label">{{ translate("page.admin.edit_event.event_type") }}:</label>
                <MultiselectInput
                    :placeholder="translate('page.admin.edit_event.select_event_type')"
                    :multiple="true"
                    :options="eventTypes"
                    v-model="selectedEventTypes"
                />
            </div>
        </div>
        <div class="column is-6">
            <div :class="getFormGroupClass('urlSlug')">
                <label class="label">{{ translate("page.admin_edit_event.url_slug") }}</label>
                <input class="input" v-model="urlSlug" maxlength="100" />
                <span class="help">{{ getValidationMessage("urlSlug") }}</span>
            </div>
        </div>

        <div class="column is-3">
            <div :class="getFormGroupClass('timezone')">
                <label class="label">{{ translate("page.admin_edit_event.timezone") }}</label>
                <input readonly class="input" :value="timezone" @click="openTimezonePicker" />
            </div>
        </div>

        <div class="column is-3">
            <div :class="getFormGroupClass('place')">
                <label class="label">{{ translate("page.admin_edit_event.place") }}</label>
                <input class="input has-pointer-cursor" readonly :value="place" @click="openEventLocationModal" />
                <span class="help">{{ getValidationMessage("place") }}</span>
            </div>
        </div>

        <div class="column is-3">
            <div :class="getFormGroupClass('eventDate')">
                <label class="label">{{ translate("page.admin_edit_event.event_date") }}</label>
                <DateTimePicker class="input" v-model="eventStartDate" />
                <span class="help">{{ getValidationMessage("eventDate") }}</span>
            </div>
        </div>
        <div class="column is-3">
            <div :class="getFormGroupClass('eventEndDate')">
                <label class="label">{{ translate("page.admin_edit_event.event_end_date") }}</label>
                <DateTimePicker class="input" v-model="eventEndDate" />
                <span class="help">{{ getValidationMessage("eventEndDate") }}</span>
            </div>
        </div>

        <div class="column is-3">
            <div :class="getFormGroupClass('contactInfo')">
                <label class="label">{{ translate("page.event_details.contact_info") }}:</label>
                <input class="input has-pointer-cursor" readonly :value="printableContactInfo" @click="openContactInfoModal" />
                <span class="help">{{ getValidationMessage("contactInfo") }}</span>
            </div>
        </div>

        <TimezonePicker ref="timezonePicker" v-model="timezone" />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import DateTimePicker from "../../Common/DateTimePicker.vue";
    import TimezonePicker from "../TimezonePicker.vue";
    import MultiselectInput from "../../Common/MultiselectInput.vue";
    import { translate } from "../../Common/translations";
    import { convertToTimezone, convertFromTimezone, getDateFromDateTime } from "../../Common/dateTimeUtils";
    import { convertToUrlSlug } from "../../Common/urlSlug";
    import { CONTACT_INFO_OPEN, EVENT_LOCATION_OPEN, eventBus } from "../eventBus";
    import { EVENT_TYPES } from "../../Common/consts";
    import { convertEventTypesToObjects, useEditEventStore, type EventTypeObject } from "../editEventStore";

    const store = useEditEventStore();

    const timezonePicker = ref<ComponentExposed<typeof TimezonePicker> | null>(null);

    const eventTypes = ref<EventTypeObject[]>(convertEventTypesToObjects(EVENT_TYPES).sort((a, b) => a.label.localeCompare(b.label)));

    const validationErrors = computed({
        get: () => store.validationErrors,
        set: value => {
            store.validationErrors = value;
        },
    });

    const fullName = computed({
        get: () => store.event.fullName,
        set: (value: string) => {
            store.setEventProperty({ name: "fullName", value });
        },
    });

    const urlSlug = computed({
        get: () => store.event.urlSlug,
        set: (value: string) => {
            store.setEventProperty({ name: "urlSlug", value });
        },
    });

    const timezone = computed({
        get: () => store.event.timezone,
        set: (value: string) => {
            store.setEventProperty({ name: "timezone", value });
        },
    });

    const place = computed({
        get: () => store.event.place,
        set: (value: string) => {
            store.setEventProperty({ name: "place", value });
        },
    });

    const selectedEventTypes = computed({
        get: () => store.eventTypes,
        set: (value: EventTypeObject[]) => {
            store.setEventTypes(value);
        },
    });

    const eventStartDate = computed({
        get: () => convertToTimezone(store.event.eventDate.startDate ?? "", timezone.value),
        set: (value: string) => {
            const valueWithTime = value + " 00:00:00";
            store.setEventDate({
                name: "startDate",
                value: convertFromTimezone(valueWithTime, timezone.value),
            });
        },
    });

    const eventEndDate = computed({
        get: () => convertToTimezone(store.event.eventDate.endDate ?? "", timezone.value),
        set: (value: string) => {
            const valueWithTime = value + " 23:59:59";
            store.setEventDate({
                name: "endDate",
                value: convertFromTimezone(valueWithTime, timezone.value),
            });
        },
    });

    const printableContactInfo = computed(() => {
        return [store.event.contactInfo?.email1, store.event.contactInfo?.phone1].filter(el => !!el).join(", ");
    });

    watch(fullName, () => {
        automaticallyCompleteUrlSlug();
    });

    watch(eventStartDate, () => {
        const initStartDateValue = store.initEventStartDate;
        const utcStartDate = store.event.eventDate.startDate;

        if (initStartDateValue && initStartDateValue.value !== utcStartDate) {
            onPickedDifferentEventStartDate();
        }
    });

    function onPickedDifferentEventStartDate() {
        // Setters to dates take current timezone.
        eventEndDate.value = getDateFromDateTime(eventStartDate.value);
    }

    function automaticallyCompleteUrlSlug() {
        // if we deal with freshly created draft, auto-fill url slug, once it's saved - don't change it.
        if (!store.urlSlugDefinedForDraft) {
            return;
        }

        urlSlug.value = convertToUrlSlug(fullName.value);
    }

    function openEventLocationModal() {
        eventBus.$emit(EVENT_LOCATION_OPEN);
    }

    function openContactInfoModal() {
        eventBus.$emit(CONTACT_INFO_OPEN);
    }

    function openTimezonePicker() {
        timezonePicker.value?.open();
    }

    function getFormGroupClass(field: string) {
        if (validationErrors.value[field]) {
            return "field has-error";
        }

        return "field";
    }

    function getValidationMessage(field: string) {
        return validationErrors.value[field] ? validationErrors.value[field].join(" ") : "";
    }
</script>

<style scoped></style>
